import React from 'react';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { marketingLogos, marketingPalette } from '../../content/marketing';

const bodyFontFamily = '"Manrope", "Segoe UI", sans-serif';
const palette = marketingPalette;

const footerGroups = [
  {
    title: 'Explore',
    links: [
      { label: 'Home', path: '/' },
      { label: 'About', path: '/about' },
      { label: 'Downloads', path: '/downloads' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Company overview', href: '/company.html' },
      { label: 'Log in', path: '/login' },
      { label: 'Terms', path: '/terms' },
      { label: 'Privacy', path: '/privacy' },
    ],
  },
];

function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        background: 'linear-gradient(180deg, #123d67 0%, #0d2d4e 100%)',
        color: palette.white,
        borderTop: `1px solid ${alpha('#ffffff', 0.06)}`,
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 5, md: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 5 }}>
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src={marketingLogos.compactLight}
              alt="Virtual Medic"
              sx={{
                width: { xs: 150, sm: 168 },
                maxWidth: '100%',
                mb: 2.5,
              }}
            />
            <Typography
              sx={{
                maxWidth: 420,
                color: alpha(palette.white, 0.80),
                lineHeight: 1.78,
                fontFamily: bodyFontFamily,
              }}
            >
              An AI-powered consultation practice platform for medical students and medical schools,
              designed to make structured feedback more accessible, repeatable, and realistic.
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: alpha(palette.heroBlueSoft, 0.92),
                fontWeight: 700,
                fontFamily: bodyFontFamily,
              }}
            >
              Built for modern medical education.
            </Typography>
          </Grid>

          {footerGroups.map((group) => (
            <Grid item xs={6} md={2} key={group.title}>
              <Typography
                sx={{
                  mb: 1.5,
                  color: palette.heroBlueSoft,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: '0.78rem',
                  fontFamily: bodyFontFamily,
                }}
              >
                {group.title}
              </Typography>
              <Stack spacing={1.1}>
                {group.links.map((link) => {
                  const navigationProps = link.href
                    ? { component: 'a', href: link.href }
                    : { component: Link, to: link.path };

                  return (
                    <Typography
                      key={link.label}
                      {...navigationProps}
                      sx={{
                        textDecoration: 'none',
                        color: alpha(palette.white, 0.78),
                        fontWeight: 700,
                        fontFamily: bodyFontFamily,
                        '&:hover': {
                          color: '#ffffff',
                        },
                      }}
                    >
                      {link.label}
                    </Typography>
                  );
                })}
              </Stack>
            </Grid>
          ))}

          <Grid item xs={12} md={2}>
            <Typography
              sx={{
                mb: 1.5,
                color: palette.heroBlueSoft,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.78rem',
                fontFamily: bodyFontFamily,
              }}
            >
              Contact
            </Typography>
            <Stack spacing={1.1}>
              <Typography
                component="a"
                href="mailto:admin@virtualmedic.uk"
                sx={{
                  textDecoration: 'none',
                  color: alpha(palette.white, 0.78),
                  fontWeight: 700,
                  fontFamily: bodyFontFamily,
                  '&:hover': {
                    color: '#ffffff',
                  },
                }}
              >
                admin@virtualmedic.uk
              </Typography>
              <Typography
                component="a"
                href="tel:+447828474621"
                sx={{
                  textDecoration: 'none',
                  color: alpha(palette.white, 0.78),
                  fontWeight: 700,
                  fontFamily: bodyFontFamily,
                  '&:hover': {
                    color: '#ffffff',
                  },
                }}
              >
                +44 7828 474621
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            pt: 2.5,
            borderTop: `1px solid ${alpha('#ffffff', 0.10)}`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Typography sx={{ color: alpha(palette.white, 0.66), fontFamily: bodyFontFamily }}>
            Copyright {year} Virtual Medic. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2.25} flexWrap="wrap" useFlexGap>
            <Typography
              component={Link}
              to="/terms"
              sx={{
                textDecoration: 'none',
                color: alpha(palette.white, 0.66),
                fontWeight: 700,
                fontFamily: bodyFontFamily,
                '&:hover': {
                  color: '#ffffff',
                },
              }}
            >
              Terms
            </Typography>
            <Typography
              component={Link}
              to="/privacy"
              sx={{
                textDecoration: 'none',
                color: alpha(palette.white, 0.66),
                fontWeight: 700,
                fontFamily: bodyFontFamily,
                '&:hover': {
                  color: '#ffffff',
                },
              }}
            >
              Privacy
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
